/**
 * The `@domain templates` endpoints (D1.1, ARCFE-1012).
 *
 * All 34 moved together, name-intact, over the `@heidi/api/client` registry —
 * the same singletons the hub used, so interceptors, auth and base URLs are
 * unchanged.
 *
 * FOUR SESSION ENDPOINTS DELIBERATELY DID NOT COME: `generateDocument`,
 * `updateConsultNote`, `getSessionTranscript` and `getDictationCleanupModes`
 * appear in this hub's templates neighbourhood because template flows call
 * them, but they are Session's. `generateDocument` in particular is the exact
 * symbol Session's E3 ruled stays app-side (ARCFE-1004, port inversion). The
 * family selector excludes all four BY NAME rather than trusting the noun to
 * miss them — two lanes claiming one symbol is the ARCFE-925 not-a-partition
 * failure.
 */
import { getScribeHttpClients } from '@heidi/api/client';
// `SortOrder` is the shared schema enum (`Shared__enums__enums__SortOrder`), not a
// templates-owned type — imported from the schema door, not re-declared.
// Both of these are SCHEMA types, not templates-owned ones. `TemplateSortBy`
// matters: `scribeAPI.ts` also exports a hand-authored enum of the same name
// (a narrower app-facing subset with different member names), and the hub
// endpoint bound the SCHEMA one. Binding the app enum here would have been a
// silent signature change that a body-for-body diff cannot see, because the
// text is identical and only the resolved type differs. tsc caught it.
import type {
  Shared__enums__enums__SortOrder as SortOrder,
  TemplateSortBy,
} from '@heidi/schema/ml-scribe';
import axios from 'axios';
import type {
  BatchLikeStatusResponse,
  CreateLatestVersionControlRequest,
  CreateMKTemplateRequestPayload,
  CreatePublicTemplateSuggestionsRequest,
  CreateSharedTemplateRequest,
  CreateSharedTemplateResponse,
  CreateTemplateCreationRecordByTemplateIdResponse,
  CreateTemplateCreationRecordRequest,
  CreateTemplateCreationRecordResponse,
  CreateTemplateRequest,
  CreateTemplateSuggestionsRequest,
  CreateTemplateSuggestionsResponse,
  EditTemplatePublicRequest,
  EditTemplatePublicResponse,
  ExtractJsonFormFieldsResponse,
  ExtractPdfFormFieldsResponse,
  ExtractXmlFormFieldsResponse,
  FillFormDemoFromUploadRequest,
  FillFormDemoRequest,
  FillFormDemoResponse,
  FillJsonFormDemoFromUploadRequest,
  FillJsonFormDemoRequest,
  FillJsonFormDemoResponse,
  FillXmlFormDemoFromUploadRequest,
  FillXmlFormDemoRequest,
  FillXmlFormDemoResponse,
  GenerateExampleNotePublicRequest,
  GenerateExampleNotePublicResponse,
  GenerateTemplatePublicRequest,
  GenerateTemplatePublicResponse,
  GetSharedTemplateResponse,
  GetTemplateCreationRecordByTemplateIdResponse,
  GetTemplateCreationRecordResponse,
  GetTemplatesResponse,
  LikeResponse,
  LikeStatusResponse,
  MKTemplateRequestResponse,
  PDFTemplateUrlRequest,
  ProcessUploadedDocumentResponse,
  SearchTemplatesResponse,
  ShareTemplateByEmailRequest,
  ShareTemplateByEmailResponse,
  TemplateJsonSourceResponse,
  TemplateResponse,
  TemplateSearchFilterMetadataResponse,
  TemplateVersionControlResponse,
  TemplateXmlSourceResponse,
  UpdateTemplateCreationRecordRequest,
  UpdateTemplateCreationRecordResponse,
  UpdateTemplateRequest,
  UpdateTemplateVersionControlRequest,
} from '../../contracts';

/** @domain templates */
export const getTemplates = (specialtyId?: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplatesResponse>(`/templates/`, {
    params: {
      specialty_id: specialtyId,
    },
  });
};

/** @domain templates */
export const getPinnedTemplates = () => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplatesResponse>(
    `/templates/pinned`
  );
};

/** @domain templates */
export const updatePinnedTemplateOrder = (templateIds: string[]) => {
  return getScribeHttpClients().scribeHttpV2.put(`/templates/pinned/order`, {
    template_ids: templateIds,
  });
};

/** @domain templates */
export const getInitialPinnedTemplates = () => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplatesResponse>(
    `/templates/initial-pinned-templates`
  );
};

/** @domain templates */
export const getRecommendedTemplates = (params: {
  specialtyId?: string;
  language?: string;
  country?: string;
  limit?: number;
}) => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplatesResponse>(
    `/templates/recommended-templates`,
    {
      params: {
        specialty_id: params.specialtyId,
        language: params.language,
        country: params.country,
        limit: params.limit,
      },
    }
  );
};

/** @domain templates */
export const getTemplateById = (id: string) => {
  return getScribeHttpClients().scribeHttpV2.get<TemplateResponse>(`/templates/${id}`);
};

/** @domain templates */
export const getConsultNoteTemplates = (specialtyId?: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplatesResponse>(
    `templates/consult-note-templates`,
    {
      params: {
        specialty_id: specialtyId,
      },
    }
  );
};

/** @domain templates */
export const deleteTemplateV2 = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.delete<TemplateResponse>(
    `/templates/${templateId}`
  );
};

/** @domain templates */
export const getDocumentTemplates = () => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplatesResponse>(
    `templates/document-templates`
  );
};

/** @domain templates */
export const shareTemplateByEmail = (data: ShareTemplateByEmailRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<ShareTemplateByEmailResponse>(
    '/templates/share-email',
    data
  );
};

/**
 * Creates a shared template link. Requires authentication.
 * The server copies the template data and returns a short ID.
 * @domain templates
 */
export const createSharedTemplate = (data: CreateSharedTemplateRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<CreateSharedTemplateResponse>(
    '/templates/shared',
    data
  );
};

/** @domain templates */
export const createTemplate = ({
  template,
  saveAnyway = false,
  signal,
}: {
  template: CreateTemplateRequest;
  saveAnyway: boolean;
  signal?: AbortSignal;
}) => {
  return getScribeHttpClients().scribeHttpV2.post<TemplateResponse>(
    `/templates/?save_anyway=${saveAnyway}`,
    template,
    { signal }
  );
};

/** @domain templates */
export const editTemplate = ({
  template,
  templateId,
  saveAnyway = false,
  signal,
}: {
  template: UpdateTemplateRequest;
  templateId: string;
  saveAnyway: boolean;
  signal?: AbortSignal;
}) => {
  return getScribeHttpClients().scribeHttpV2.patch<TemplateResponse>(
    `/templates/${templateId}?save_anyway=${saveAnyway}`,
    template,
    { signal }
  );
};

/** @domain templates */
export const editTemplateFavoriteStatus = (templateId: string, status: boolean) => {
  return getScribeHttpClients().scribeHttpV2.post<TemplateResponse>(
    `/templates/${templateId}/favour`,
    {
      is_favorite: status,
    }
  );
};

/** @domain templates */
export const editTemplatePinStatus = (templateId: string, status: boolean) => {
  return getScribeHttpClients().scribeHttpV2.post<TemplateResponse>(
    `/templates/${templateId}/pin`,
    {
      is_pinned: status,
    }
  );
};

/** @domain templates */
export const setInitialPinnedTemplates = () => {
  return getScribeHttpClients().scribeHttpV2.post<{
    success: boolean;
    pinned_count: number;
    message: string;
    templates: TemplateResponse[];
  }>(`/templates/set-initial-pinned-templates`);
};

/** @domain templates */
export const getOrganizationTemplates = (input: {
  keyword?: string;
  sortBy?: TemplateSortBy;
  order?: SortOrder;
  page?: number;
  pageSize?: number;
  types?: string[];
  creatorIds?: string[];
  visibilities?: string[];
  organizationId: string;
  sharedTeamIds?: string[];
}) => {
  return getScribeHttpClients().scribeHttpV2.get<SearchTemplatesResponse>(
    `/templates/search`,
    {
      params: {
        keyword: input.keyword,
        sort_by: input.sortBy,
        order: input.order,
        page: input.page,
        page_size: input.pageSize,
        types: input.types,
        creator_user_ids: input.creatorIds,
        visibilities: input.visibilities,
        organization_id: input.organizationId,
        shared_team_ids: input.sharedTeamIds,
      },
    }
  );
};

/** @domain templates */
export const getOrganizationTemplatesMetadata = (organizationId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<TemplateSearchFilterMetadataResponse>(
    `/templates/search/filter-metadata`,
    {
      params: {
        organization_id: organizationId,
      },
    }
  );
};

/** @domain templates */
export const getInternalTemplateById = (id: string) => {
  return getScribeHttpClients().scribeHttpV2.get<TemplateResponse>(
    `/templates/system/${id}`
  );
};

/** @domain templates */
export const createTemplateCreationRecord = (
  request: CreateTemplateCreationRecordRequest
) => {
  return getScribeHttpClients().scribeHttpV2.post<CreateTemplateCreationRecordResponse>(
    '/templates/template-creation-record',
    request
  );
};

/** @domain templates */
export const getTemplateCreationRecordById = (templateCreationRecordId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplateCreationRecordResponse>(
    `/templates/template-creation-record/${templateCreationRecordId}`
  );
};

/** @domain templates */
export const createTemplateSuggestions = (request: CreateTemplateSuggestionsRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<CreateTemplateSuggestionsResponse>(
    '/templates/template-creation-record/suggestions',
    request
  );
};

/** @domain templates */
export const createPublicTemplateSuggestions = (
  request: CreatePublicTemplateSuggestionsRequest
) => {
  return getScribeHttpClients().scribeHttpV2.post<CreateTemplateSuggestionsResponse>(
    '/templates/template-creation-record/suggestions/public',
    request
  );
};

/** @domain templates */
export const generateTemplatePublic = (request: GenerateTemplatePublicRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<GenerateTemplatePublicResponse>(
    '/templates/template-creation-record/generate/public',
    request
  );
};

/** @domain templates */
export const editTemplatePublic = (request: EditTemplatePublicRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<EditTemplatePublicResponse>(
    '/templates/template-creation-record/edit/public',
    request
  );
};

/** @domain templates */
export const generateExampleNotePublic = (request: GenerateExampleNotePublicRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<GenerateExampleNotePublicResponse>(
    '/templates/template-creation-record/example-note/public',
    request
  );
};

/** @domain templates */
export const createTemplateCreationRecordByTemplateId = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.post<CreateTemplateCreationRecordByTemplateIdResponse>(
    `/templates/template-creation-record/template/${templateId}`
  );
};

/** @domain templates */
export const getTemplateCreationRecordByTemplateId = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetTemplateCreationRecordByTemplateIdResponse>(
    `/templates/template-creation-record/template/${templateId}`
  );
};

/** @domain templates */
export const updateTemplateCreationRecord = ({
  templateCreationRecordId,
  data,
  signal,
}: {
  templateCreationRecordId: string;
  data: UpdateTemplateCreationRecordRequest;
  signal?: AbortSignal;
}) => {
  return getScribeHttpClients().scribeHttpV2.patch<UpdateTemplateCreationRecordResponse>(
    `/templates/template-creation-record/${templateCreationRecordId}`,
    data,
    { signal }
  );
};

/** @domain templates */
export const createLatestVersionControl = ({
  templateCreationRecordId,
  data,
  signal,
}: {
  templateCreationRecordId: string;
  data: CreateLatestVersionControlRequest;
  signal?: AbortSignal;
}) => {
  return getScribeHttpClients().scribeHttpV2.post<TemplateVersionControlResponse>(
    `/templates/template-creation-record/${templateCreationRecordId}/version-control`,
    data,
    { signal }
  );
};

/**
 * `GET /templates/:templateId/xml-source` — the stored XML source of an XML
 * form template. Sources are excluded from template list/detail responses to
 * keep them lightweight; fetch here at the moment the source is needed
 * (download, share-link build).
 *
 * @domain templates
 */
export const getXmlTemplateSource = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<TemplateXmlSourceResponse>(
    `/templates/${templateId}/xml-source`
  );
};

/**
 * Extracts form fields from an uploaded XML file.
 *
 * Multipart, field name `xml_file` — mirroring `/pdf-template/extract-fields`,
 * whose field is `pdf_file`. The response echoes the XML back as
 * `xml_source`: XML templates keep their document in Mongo rather than S3, so
 * this string IS the template body and the caller sends it to `/templates`.
 *
 * @domain templates
 */
export const extractXmlFormFields = (file: File) => {
  const formData = new FormData();
  formData.append('xml_file', file);

  return getScribeHttpClients().scribeHttpV2.post<ExtractXmlFormFieldsResponse>(
    '/xml-template/extract-fields',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/**
 * Extracts form fields from an uploaded PDF (or DOCX), the form editor's
 * Textract-backed extraction.
 *
 * Multipart, field name `pdf_file`. `forceLlmExtraction` asks the backend to
 * flatten the PDF's own AcroForm widgets and always run LLM detection.
 *
 * @domain templates
 */
export const extractPdfFormFields = (
  file: File,
  options?: { forceLlmExtraction?: boolean }
) => {
  const formData = new FormData();
  formData.append('pdf_file', file);
  if (options?.forceLlmExtraction) {
    formData.append('force_llm_extraction', 'true');
  }

  return getScribeHttpClients().scribeHttpV2.post<ExtractPdfFormFieldsResponse>(
    '/pdf-template/extract-fields',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/**
 * Extracts form fields from an uploaded PDF so the XML review step can save
 * an XFA (LiveCycle) form. The response is only an XFA form when
 * `form_format` is `xfa_pdf` and `xml_source` is set; the caller checks both.
 *
 * @domain templates
 */
export const extractXfaPdfFormFields = (file: File) => extractPdfFormFields(file);

/**
 * `GET /templates/:templateId/json-source` — the stored JSON source of a JSON
 * form template. List and detail omit `json_source`. Unlike the XML source
 * read, the response has no revision. Non-JSON and AUTO_MODE answer 404.
 *
 * @domain templates
 */
export const getJsonTemplateSource = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<TemplateJsonSourceResponse>(
    `/templates/${templateId}/json-source`
  );
};

/**
 * Extracts form fields from an uploaded JSON file.
 *
 * Multipart, field name `json_file`. The response echoes the normalized
 * document as `json_source`. Unlike XML extract, this endpoint emits no
 * tracking event — do not add one on the client either.
 *
 * @domain templates
 */
export const extractJsonFormFields = (file: File) => {
  const formData = new FormData();
  formData.append('json_file', file);

  return getScribeHttpClients().scribeHttpV2.post<ExtractJsonFormFieldsResponse>(
    '/json-template/extract-fields',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/** @domain templates */
export const processUploadedDocument = (files: File[]) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  return getScribeHttpClients().scribeHttpV2.post<ProcessUploadedDocumentResponse>(
    '/templates/template-creation-record/process-uploaded-document',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/** @domain templates */
export const processUploadedDocumentPublic = (files: File[]) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  return getScribeHttpClients().scribeHttpV2.post<ProcessUploadedDocumentResponse>(
    '/templates/template-creation-record/process-uploaded-document/public',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/** @domain templates */
export const deleteTemplateVersionControl = (versionControlId: string) => {
  return getScribeHttpClients().scribeHttpV2.delete(
    `/templates/template-creation-record/version-control/${versionControlId}`
  );
};

/** @domain templates */
export const updateTemplateVersionControl = ({
  versionControlId,
  request,
}: {
  versionControlId: string;
  request: UpdateTemplateVersionControlRequest;
}) => {
  return getScribeHttpClients().scribeHttpV2.patch<TemplateVersionControlResponse>(
    `/templates/template-creation-record/version-control/${versionControlId}`,
    request
  );
};

// ---------------------------------------------------------------------------
// DOMAIN RULING — two `@domain REVIEW` endpoints carved to templates
// (D2.1, ARCFE-1015)
//
// `src/api/CLAUDE.md`: "@domain REVIEW is a burn-down placeholder … carve-time
// rulings live in the package modules". This is that ruling, made rather than
// dodged.
//
// Both are unambiguously templates BY RESOURCE, which is the test the
// resource-owner rule (EN1) sets — not by path prefix and not by who calls
// them. `/templates/shared/{id}` reads a shared template;
// `/pdf-template` mints an upload URL for a PDF template. Neither has a
// competing claimant: no other lane's selector matches them, and no other
// domain owns the resource.
//
// `getSharedTemplate` keeps `openApiHttp` — the UNAUTHENTICATED registry
// client. The shared-template page is public by design, so swapping it to
// `scribeHttpV2` would attach auth interceptors to a deliberately anonymous
// read. It stays on the registry (it is a scribe client), so it does NOT cross
// the `configureTemplatesData` seam; that seam is for `publicCommunityApi`,
// which is a different client entirely.
// ---------------------------------------------------------------------------

/**
 * Fetches a shared template by ID. No authentication required.
 * This uses the open API instance which doesn't require auth.
 *
 * A shared template record only exists in the sharer's region. When the share
 * link carries that region, the caller resolves its open-api base URL (one of
 * the fixed regional hosts) and passes it as `sourceOpenApiBaseUrl`. That read
 * goes out on plain axios: the registry clients attach the viewer's widget
 * credentials on every request, and those must not travel to another
 * region's host.
 */
export const getSharedTemplate = (id: string, sourceOpenApiBaseUrl?: string) => {
  const path = `templates/shared/${id}`;
  if (sourceOpenApiBaseUrl) {
    return axios.get<GetSharedTemplateResponse>(
      `${sourceOpenApiBaseUrl.replace(/\/+$/, '')}/${path}`
    );
  }
  return getScribeHttpClients().openApiHttp.get<GetSharedTemplateResponse>(`/${path}`);
};

export const getUploadTemplateURL = (pdfTemplateUrlRequest: PDFTemplateUrlRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<string>(
    '/pdf-template',
    pdfTemplateUrlRequest
  );
};

// ---------------------------------------------------------------------------
// Template-gallery LIKES (S2, ARCFE-1017)
//
// These four lived in `publicCommunityApi` alongside the gallery reads, but
// they are a different thing: the gallery is unauthenticated (`globalHttp`),
// the likes are authenticated (`scribeHttpV2`). So they come to the package
// over the REGISTRY and never cross the `configureTemplatesData` community
// seam. The S2 cohort splits by function, not by module.
// ---------------------------------------------------------------------------

export const likeTemplate = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.post<LikeResponse>(
    `/template-gallery/templates/${templateId}/like`
  );
};

export const unlikeTemplate = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.delete<LikeResponse>(
    `/template-gallery/templates/${templateId}/like`
  );
};

export const getLikeStatus = (templateId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<LikeStatusResponse>(
    `/template-gallery/templates/${templateId}/like`
  );
};

export const getBatchLikeStatus = (templateIds: string[]) => {
  const params = new URLSearchParams();
  templateIds.forEach((id) => params.append('template_ids', id));
  return getScribeHttpClients().scribeHttpV2.get<BatchLikeStatusResponse>(
    `/template-gallery/templates/likes/batch`,
    {
      params,
    }
  );
};

/**
 * DOMAIN RULING (ARCFE-1019) — `createMKTemplateRequest` was `@domain REVIEW`.
 * `/mk-template-requests/` is a template request: templates by RESOURCE, with
 * no competing claimant. Multipart, so it keeps its explicit Content-Type.
 */
export const createMKTemplateRequest = (payload: CreateMKTemplateRequestPayload) => {
  const formData = new FormData();
  formData.append('template_name', payload.template_name);
  formData.append('description', payload.description);

  if (payload.existing_template_id) {
    formData.append('existing_template_id', payload.existing_template_id);
  }

  if (payload.additional_notes) {
    formData.append('additional_notes', payload.additional_notes);
  }

  if (payload.pdf_file) {
    formData.append('pdf_file', payload.pdf_file);
  }

  return getScribeHttpClients().scribeHttpV2.post<MKTemplateRequestResponse>(
    '/mk-template-requests/',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/**
 * `POST /pdf-template-demo/fill-json-form` — the public community demo fill
 * for a JSON form template (MOD-1745).
 *
 * Unauthenticated in effect: the community page is anonymous, and ml-scribe
 * resolves a demo user itself. It still goes over `scribeHttpV2` rather than
 * the community client, because the endpoint is ml-scribe's, not the
 * gallery's.
 *
 * @domain templates
 */
export const fillJsonFormDemo = (request: FillJsonFormDemoRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<FillJsonFormDemoResponse>(
    '/pdf-template-demo/fill-json-form',
    request
  );
};

/**
 * `POST /pdf-template-demo/fill-json-form-from-upload` — the same demo fill
 * from one uploaded audio or document file instead of a typed transcript.
 *
 * `form_fields` is sent as a JSON string: the rest of the payload is
 * multipart, and ml-scribe parses this field with `json.loads` before
 * validating it.
 *
 * @domain templates
 */
export const fillJsonFormDemoFromUpload = (
  request: FillJsonFormDemoFromUploadRequest
) => {
  const formData = new FormData();
  formData.append('source', request.source);
  formData.append('file', request.file);
  formData.append('json_source', request.json_source);
  formData.append('form_fields', JSON.stringify(request.form_fields));
  if (request.specialty_name) {
    formData.append('specialty_name', request.specialty_name);
  }
  if (request.country) {
    formData.append('country', request.country);
  }
  if (request.language) {
    formData.append('language', request.language);
  }

  return getScribeHttpClients().scribeHttpV2.post<FillJsonFormDemoResponse>(
    '/pdf-template-demo/fill-json-form-from-upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

/** @domain templates */
export const fillFormDemo = (request: FillFormDemoRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<FillFormDemoResponse>(
    '/pdf-template-demo/fill-form',
    request
  );
};

/** @domain templates */
export const fillFormDemoFromUpload = (request: FillFormDemoFromUploadRequest) => {
  const formData = new FormData();
  formData.append('source', request.source);
  formData.append('file', request.file);
  formData.append('s3_object_key', request.s3_object_key);
  formData.append('form_fields', JSON.stringify(request.form_fields));
  if (request.specialty_name) {
    formData.append('specialty_name', request.specialty_name);
  }
  if (request.country) {
    formData.append('country', request.country);
  }
  if (request.language) {
    formData.append('language', request.language);
  }

  return getScribeHttpClients().scribeHttpV2.post<FillFormDemoResponse>(
    '/pdf-template-demo/fill-form-from-upload',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
};

/** @domain templates */
export const fillXmlFormDemo = (request: FillXmlFormDemoRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<FillXmlFormDemoResponse>(
    '/pdf-template-demo/fill-xml-form',
    request
  );
};

/** @domain templates */
export const fillXmlFormDemoFromUpload = (request: FillXmlFormDemoFromUploadRequest) => {
  const formData = new FormData();
  formData.append('source', request.source);
  formData.append('file', request.file);
  formData.append('xml_source', request.xml_source);
  formData.append('form_fields', JSON.stringify(request.form_fields));
  if (request.specialty_name) {
    formData.append('specialty_name', request.specialty_name);
  }
  if (request.country) {
    formData.append('country', request.country);
  }
  if (request.language) {
    formData.append('language', request.language);
  }

  return getScribeHttpClients().scribeHttpV2.post<FillXmlFormDemoResponse>(
    '/pdf-template-demo/fill-xml-form-from-upload',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
};
